import { fromJS, Map, List } from 'immutable';
import Account from 'models/AccountModel';
import Entity from 'models/Entity';
import Lender from 'models/LenderModel';
import FundingSearch from 'models/FundingSearchModel';
import Facility from 'models/FacilityModel';
import ConnectionHistoryItem from 'models/ConnectionHistoryItemModel';
import differenceInSeconds from 'date-fns/differenceInSeconds';

import { generateReducer } from '../../utils/HttpReducerHelper';
import { normalizeIds, normalizeModelList } from '../../utils/Normalizer';

import {
  activeLenders,
  clientsAccounts,
  businessFundingSearches,
  creditLinesFacilityMap,
  facilitiesMap,
  saveFacilityType,
  ADD_WIZZARD_CREDIT_FACILITY,
  ADD_WIZZARD_LEDGER_PROVIDER_FACILITY,
  CLOSE_WIZZARD,
  CLOSE_WIZZARD_DATA_LOADING_ERROR_MODAL,
  CLOSE_WIZZARD_DATA_LOADING_MODAL,
  OPEN_WIZZARD,
  OPEN_WIZZARD_DATA_LOADING_ERROR_MODAL,
  OPEN_WIZZARD_DATA_LOADING_MODAL,
  REMOVE_WIZZARD_CREDIT_FACILITY,
  REMOVE_WIZZARD_LEDGER_PROVIDER_FACILITY,
  SET_WIZZARD_ACCOUNTING_SOLUTION,
  SET_WIZZARD_CLIENT,
  SET_WIZZARD_PROVIDER_MAP_VALIDITY,
  SAVE_INTEGRATION_PAYLOAD,
  RESET_INTEGRATION_PAYLOAD,
  CONNECTION_HISTORY_LIST_ITEMS,
  ADD_CONNECTION_HISTORY_LIST_ITEM,
  REMOVE_CONNECTION_HISTORY_LIST_ITEM,
  UPDATE_CONNECTION_HISTORY_LIST_ITEM,
  SSE_UPDATE_SNAPSHOT_GENERATION_PROGRESS,
  SET_EDIT_FACILITY_WIZZARD,
} from './types';

import { searchCompany, CLEAR_COMPANIES } from './add-new-wizzard.types';

export * from './actions';
export * from './add-new-wizzard.actions';

const getCachedConnectionHistory = () => {
  const cachedItems = JSON.parse(localStorage.getItem(CONNECTION_HISTORY_LIST_ITEMS)) || {};
  let result = new Map();

  Object.values(cachedItems)
    .filter((value) => differenceInSeconds(new Date(), new Date(value.updatedAt)) < 300)
    .forEach((value) => {
      result = result.set(value.id, ConnectionHistoryItem.deserialize(value));
    });

  return result;
};

let initialState = fromJS({
  connectWizzard: {
    isOpen: false,
    startPath: false,
    options: {},
  },
  addNewClient: {
    byId: {},
    companies: {
      ids: [],
      network: { loading: false, error: false },
    },
  },
  accountingSolution: '',
  client: null,
  byId: {},
  availableAccounts: {
    ids: [],
    network: { loading: false, error: false },
  },
  fundingSearches: {
    data: [],
    network: { loading: false, error: false },
  },
  activeLenders: {
    data: [],
    network: { loading: false, error: false },
  },
  creditLinesFacilityMap: {
    accounts: [],
    ledgerProvidersMap: [],
    network: { loading: false, error: false, success: null },
    valid: true,
  },
  facilities: [],
  activeEditFacilityId: null,
  isDataLoadingModalOpen: false,
  isErrorLoadingModalOpen: false,
  integrationFilePayload: null,
  connectionHistoryById: {},
});
initialState = initialState.set('connectionHistoryById', getCachedConnectionHistory());

const availableAccountsReducer = generateReducer('availableAccounts', Account.deserializeSnapshot);

const openMonitorWizzard = (state, action) =>
  state.withMutations((s) =>
    s
      .set('isOpen', true)
      .setIn(['connectWizzard', 'isOpen'], true)
      .setIn(['connectWizzard', 'startPath'], action.startPath)
      .setIn(['connectWizzard', 'options'], action.options),
  );

const setEditFacilityWizzard = (state, action) =>
  state.withMutations((s) => s.set('activeEditFacilityId', action.id));

const closeMonitorWizzard = (state) =>
  initialState
    .set('client', state.get('client'))
    .set('connectionHistoryById', state.get('connectionHistoryById'));

const setMonitorWizzardClient = (state, action) => state.set('client', action.client);

const setMonitorWizzardAccountingSolution = (state, action) =>
  state.set('accountingSolution', action.accountingSolution);

const openMonitorWizzardDataLoadingModal = (state) => state.set('isDataLoadingModalOpen', true);
const closeMonitorWizzardDataLoadingModal = (state) => state.set('isDataLoadingModalOpen', false);

const openMonitorWizzardDataLoadingErrorModal = (state) =>
  state.set('isErrorLoadingModalOpen', true);
const closeMonitorWizzardDataLoadingErrorModal = (state) =>
  state.set('isErrorLoadingModalOpen', false);

const setMonitorWizzardClientFundingSearchesRequest = (state) =>
  state.setIn(['fundingSearches', 'network', 'loading'], true);

const setMonitorWizzardClientFundingSearchesSuccess = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['fundingSearches', 'network', 'loading'], false)
      .setIn(['fundingSearches', 'network', 'error'], false)
      .setIn(
        ['fundingSearches', 'data'],
        List(action.response.map((elem) => FundingSearch.deserialize(elem))),
      ),
  );

const setMonitorWizzardClientFundingSearchesError = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['fundingSearches', 'network', 'loading'], false)
      .setIn(['fundingSearches', 'network', 'error'], action.error),
  );

const setActiveLendersRequest = (state) =>
  state.setIn(['activeLenders', 'network', 'loading'], true);

const setActiveLendersSuccess = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['activeLenders', 'network', 'loading'], false)
      .setIn(['activeLenders', 'network', 'error'], false)
      .setIn(
        ['activeLenders', 'data'],
        List(action.response.map((elem) => Lender.deserialize(elem))),
      ),
  );

const setActiveLendersError = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['activeLenders', 'network', 'loading'], false)
      .setIn(['activeLenders', 'network', 'error'], action.error),
  );

const addMonitorWizzardCreditFacility = (state, action) =>
  state.withMutations((s) =>
    s
      .updateIn(['creditLinesFacilityMap', 'accounts'], (list) =>
        list.filter((account) => account.uuid !== action.account.uuid),
      )
      .updateIn(['creditLinesFacilityMap', 'accounts'], (list) => list.push(action.account)),
  );

const removeMonitorWizzardCreditFacility = (state, action) =>
  state.updateIn(['creditLinesFacilityMap', 'accounts'], (list) =>
    list.filter((account) => account.uuid !== action.uuid),
  );

const addMonitorWizzardLedgerProviderFacility = (state, action) =>
  state.withMutations((s) =>
    s
      .updateIn(['creditLinesFacilityMap', 'ledgerProvidersMap'], (list) =>
        list.filter(
          (providerFacility) => providerFacility.providerId !== action.providerFacility.providerId,
        ),
      )
      .updateIn(['creditLinesFacilityMap', 'ledgerProvidersMap'], (list) =>
        list.push(action.providerFacility),
      ),
  );

const removeMonitorWizzardLedgerProviderFacility = (state, action) =>
  state.updateIn(['creditLinesFacilityMap', 'ledgerProvidersMap'], (list) =>
    list.filter((providerFacility) => providerFacility.providerId !== action.providerId),
  );

const setMonitorWizzardProviderFacilityValid = (state, action) =>
  state.setIn(['creditLinesFacilityMap', 'valid'], action.valid);

const setMonitorCreditLinesRequest = (state) =>
  state.setIn(['creditLinesFacilityMap', 'network', 'loading'], true);

const setMonitorCreditLinesSuccess = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['creditLinesFacilityMap', 'network', 'loading'], false)
      .setIn(['creditLinesFacilityMap', 'network', 'error'], false)
      .setIn(['creditLinesFacilityMap', 'network', 'success'], action.response)
      .setIn(['creditLinesFacilityMap', 'accounts'], List()),
  );

const setMonitorCreditLinesError = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['creditLinesFacilityMap', 'network', 'loading'], false)
      .setIn(['creditLinesFacilityMap', 'network', 'error'], action.error),
  );

const searchCompanyRequest = (state) =>
  state.setIn(['addNewClient', 'companies', 'network', 'loading'], true);

const searchCompanySuccess = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['addNewClient', 'companies', 'network', 'loading'], false)
      .setIn(['addNewClient', 'companies', 'network', 'error'], false)
      .setIn(['addNewClient', 'companies', 'ids'], normalizeIds(action.response))
      .mergeIn(['addNewClient', 'byId'], normalizeModelList(action.response, Entity.fromSearch)),
  );

const searchCompanyFailure = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['addNewClient', 'companies', 'network', 'loading'], false)
      .setIn(['addNewClient', 'companies', 'network', 'error'], action.response),
  );

const clearAddNewClientWizzardCompaniesList = (state) =>
  state.withMutations((s) =>
    s.setIn(['addNewClient', 'byId'], Map()).setIn(['addNewClient', 'companies', 'ids'], List()),
  );

const saveIntegrationPayload = (state, action) =>
  state.set('integrationFilePayload', action.payload);

const resetIntegrationPayload = (state) => state.set('integrationFilePayload', null);

const addConnectionHistoryListItem = (state, action) =>
  state.updateIn(['connectionHistoryById'], (map) => {
    const newMap = map.set(action.item.id, ConnectionHistoryItem.deserialize(action.item));

    localStorage.setItem(CONNECTION_HISTORY_LIST_ITEMS, JSON.stringify(newMap));

    return newMap;
  });

const updateConnectionHistoryListItem = (state, action) =>
  state.updateIn(['connectionHistoryById'], (map = new Map()) => {
    const { entityId, finished, percentage, withFacilities, message } = action.payload;

    if (withFacilities) {
      return map;
    }

    const id = parseInt(entityId, 10);
    const newItemById =
      map.get(id) ||
      new ConnectionHistoryItem({
        finished,
        id,
        percentage,
        updatedAt: new Date().getTime(),
        message,
      });

    const newItem = newItemById
      .set('finished', finished)
      .set('percentage', percentage)
      .set('updatedAt', new Date().getTime())
      .set('message', message);

    const newMap = map.set(id, newItem);

    localStorage.setItem(CONNECTION_HISTORY_LIST_ITEMS, JSON.stringify(newMap));

    return newMap;
  });
const removeConnectionHistoryListItem = (state, action) =>
  state.updateIn(['connectionHistoryById'], (map) => {
    const newMap = map.delete(action.id);

    localStorage.setItem(CONNECTION_HISTORY_LIST_ITEMS, JSON.stringify(newMap));

    return newMap;
  });

const setFacilitiesMapSuccess = (state, action) =>
  state.withMutations((s) =>
    s.setIn(['facilities'], List(action.payload?.map((elem) => Facility.deserialize(elem)))),
  );

const updateFacilitiesMap = (state, action) =>
  state.withMutations((s) => {
    const facilities = s.get('facilities');
    const facilitiesList = facilities
      ?.toJS()
      .map((item) => (item.id === action.payload.id ? { ...item, ...action.payload } : item));

    return s.set(['facilities'], List(facilitiesList?.map((elem) => Facility.deserialize(elem))));
  });

export default function monitorReducer(state = initialState, action) {
  switch (action.type) {
    case clientsAccounts.request:
      return availableAccountsReducer.request(state);
    case clientsAccounts.success:
      return availableAccountsReducer.success(state, action);
    case clientsAccounts.failure:
      return availableAccountsReducer.failure(state, action);

    case activeLenders.request:
      return setActiveLendersRequest(state, action);
    case activeLenders.success:
      return setActiveLendersSuccess(state, action);
    case activeLenders.failure:
      return setActiveLendersError(state, action);

    case businessFundingSearches.request:
      return setMonitorWizzardClientFundingSearchesRequest(state, action);
    case businessFundingSearches.success:
      return setMonitorWizzardClientFundingSearchesSuccess(state, action);
    case businessFundingSearches.failure:
      return setMonitorWizzardClientFundingSearchesError(state, action);

    case creditLinesFacilityMap.request:
      return setMonitorCreditLinesRequest(state);
    case creditLinesFacilityMap.success:
      return setMonitorCreditLinesSuccess(state, action);
    case creditLinesFacilityMap.failure:
      return setMonitorCreditLinesError(state, action);

    case SET_EDIT_FACILITY_WIZZARD:
      return setEditFacilityWizzard(state, action);

    case facilitiesMap.success:
      return setFacilitiesMapSuccess(state, action);

    case saveFacilityType.success:
      return updateFacilitiesMap(state, action);

    case OPEN_WIZZARD:
      return openMonitorWizzard(state, action);
    case CLOSE_WIZZARD:
      return closeMonitorWizzard(state, action);

    case SET_WIZZARD_CLIENT:
      return setMonitorWizzardClient(state, action);
    case SET_WIZZARD_ACCOUNTING_SOLUTION:
      return setMonitorWizzardAccountingSolution(state, action);

    case OPEN_WIZZARD_DATA_LOADING_MODAL:
      return openMonitorWizzardDataLoadingModal(state);
    case CLOSE_WIZZARD_DATA_LOADING_MODAL:
      return closeMonitorWizzardDataLoadingModal(state);

    case OPEN_WIZZARD_DATA_LOADING_ERROR_MODAL:
      return openMonitorWizzardDataLoadingErrorModal(state);
    case CLOSE_WIZZARD_DATA_LOADING_ERROR_MODAL:
      return closeMonitorWizzardDataLoadingErrorModal(state);

    case ADD_WIZZARD_CREDIT_FACILITY:
      return addMonitorWizzardCreditFacility(state, action);
    case REMOVE_WIZZARD_CREDIT_FACILITY:
      return removeMonitorWizzardCreditFacility(state, action);

    case ADD_WIZZARD_LEDGER_PROVIDER_FACILITY:
      return addMonitorWizzardLedgerProviderFacility(state, action);
    case REMOVE_WIZZARD_LEDGER_PROVIDER_FACILITY:
      return removeMonitorWizzardLedgerProviderFacility(state, action);

    case SET_WIZZARD_PROVIDER_MAP_VALIDITY:
      return setMonitorWizzardProviderFacilityValid(state, action);

    case searchCompany.request:
      return searchCompanyRequest(state);
    case searchCompany.success:
      return searchCompanySuccess(state, action);
    case searchCompany.failure:
      return searchCompanyFailure(state, action);

    case CLEAR_COMPANIES:
      return clearAddNewClientWizzardCompaniesList(state);

    case SAVE_INTEGRATION_PAYLOAD:
      return saveIntegrationPayload(state, action);
    case RESET_INTEGRATION_PAYLOAD:
      return resetIntegrationPayload(state);

    case ADD_CONNECTION_HISTORY_LIST_ITEM:
      return addConnectionHistoryListItem(state, action);
    case REMOVE_CONNECTION_HISTORY_LIST_ITEM:
      return removeConnectionHistoryListItem(state, action);

    case UPDATE_CONNECTION_HISTORY_LIST_ITEM:
      return updateConnectionHistoryListItem(state, action);
    case SSE_UPDATE_SNAPSHOT_GENERATION_PROGRESS:
      return updateConnectionHistoryListItem(state, action);

    default:
      return state;
  }
}
